import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LocationSection } from 'src/components/organisms';
import { TimeframeInput } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import {
  FeatureFlag,
  GlobalFilterLocation,
  GlobalFilterProps,
  InsightsDashboard,
  InsightsGlobalFilterTypes,
  LocationFilterCategory,
  State,
} from 'src/types';
import {
  adjustCountriesAfterRegionToggle,
  adjustRegionsAfterCountryToggle,
  partitionCountryNamesEnabledFirst,
  syncCountriesAfterRegionBulkSelection,
  syncRegionsAfterCountryBulkSelection,
} from 'src/utils/insights/caseLocationSelectionSync';
import { filterCaseLocationOptions } from 'src/utils/insights/filterLocationOptions';
import { getTimeframeTitle } from 'src/utils/insights/getTimeframeTitle';
import {
  DateFormats,
  diff,
  formatDate,
  getYear,
  subtractTime,
  TimeUnit,
} from 'src/utils/time';

interface CaseCountryOption {
  code: string;
  name: string;
}

interface CaseRegionOption {
  id: string;
  name: string;
  countryCodes?: string[];
}

interface Parameters {
  organisationSites: string[];
  organisationCountries: string[];
  organisationCaseCountries?: CaseCountryOption[];
  organisationCaseRegions?: CaseRegionOption[];
  oldestReportSubmittedDate: Date;
  dashboard: InsightsDashboard;
}

export const useInsightsGlobalFilters = ({
  organisationSites,
  organisationCountries,
  organisationCaseCountries = [],
  organisationCaseRegions = [],
  oldestReportSubmittedDate,
  dashboard,
}: Parameters) => {
  const countriesAndRegionsEnabled = useIsFeatureEnabled(
    FeatureFlag.COUNTRIES_AND_REGIONS,
  );

  const showTwoTabLayout = countriesAndRegionsEnabled;

  const locationFilterTitle = 'locations';

  const caseCountryCodes = useMemo(
    () => organisationCaseCountries.map((c) => c.code),
    [organisationCaseCountries],
  );
  const caseRegionIds = useMemo(
    () => organisationCaseRegions.map((r) => r.id),
    [organisationCaseRegions],
  );

  const getDefaultTimeFrame = () => {
    const today = new Date();
    const twelveMonthsAgo = subtractTime(12, TimeUnit.months, today);
    const oldestReportDate = new Date(oldestReportSubmittedDate);
    const hasReportData =
      oldestReportSubmittedDate && !Number.isNaN(oldestReportDate.getTime());
    const reportIsNewerThan12Months =
      hasReportData && oldestReportDate > twelveMonthsAgo;

    return {
      start: reportIsNewerThan12Months ? oldestReportDate : twelveMonthsAgo,
      end: today,
    };
  };

  const storedGlobalFilters = useSelector(
    (state: State) => state.insightsGlobalFilters[dashboard],
  );
  const currentLocationFilterCategory =
    storedGlobalFilters.locationFilterCategory;
  const dispatch = useDispatch();
  const defaultTimeframeSelected = getDefaultTimeFrame();
  const [globalFilterOpen, setGlobalFilterOpen] = useState(false);
  const [selectedSites, setSelectedSites] = useState(organisationSites);
  const [selectedCountries, setSelectedCountries] = useState(
    organisationCountries,
  );
  const [selectedCaseCountryCodes, setSelectedCaseCountryCodes] =
    useState(caseCountryCodes);
  const [selectedCaseRegionIds, setSelectedCaseRegionIds] =
    useState(caseRegionIds);
  const [locationsFirstLoad, setLocationsFirstLoad] = useState(false);
  const [locationTitle, setLocationTitle] = useState(
    `all ${locationFilterTitle}`,
  );
  const [selectedLocationFilterCategory, setSelectedLocationFilterCategory] =
    useState<LocationFilterCategory>(currentLocationFilterCategory);

  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeInput>(
    defaultTimeframeSelected,
  );
  const [customSelectedTimeframe, setCustomSelectedTimeframe] =
    useState<TimeframeInput>(defaultTimeframeSelected);
  const [selectedTimeOption, setSelectedTimeOption] = useState('custom');
  const [timeframeTitle, setTimeframeTitle] = useState<string | undefined>();
  const [selectedGlobalFilter, setSelectedGlobalFilter] =
    useState<GlobalFilterProps>(GlobalFilterProps.LOCATION);
  const [liveSelections, setLiveSelections] = useState<string[][] | undefined>(
    undefined,
  );

  const handleSelectionsChange = useCallback((selections: string[][]) => {
    setLiveSelections(selections);
  }, []);

  const handleCrossSelect = useCallback(
    (
      sectionIndex: number,
      option: string,
      isSelected: boolean,
      currentSelections: string[][],
    ) => {
      if (selectedLocationFilterCategory !== LocationFilterCategory.CASE) {
        return undefined;
      }
      const currentRegionNames = currentSelections[0] ?? [];
      const currentCountryNames = currentSelections[1] ?? [];
      if (sectionIndex === 0) {
        return adjustCountriesAfterRegionToggle(
          option,
          isSelected,
          organisationCaseRegions,
          organisationCaseCountries,
          currentRegionNames,
          currentCountryNames,
        );
      }
      if (sectionIndex === 1) {
        return adjustRegionsAfterCountryToggle(
          option,
          isSelected,
          organisationCaseRegions,
          organisationCaseCountries,
          currentRegionNames,
          currentCountryNames,
        );
      }
      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedLocationFilterCategory,
      organisationCaseCountries,
      organisationCaseRegions,
    ],
  );

  const handleAfterRegionBulkSelect = useCallback(
    (selections: string[][], enabledRegionNames: string[]) => {
      if (selectedLocationFilterCategory !== LocationFilterCategory.CASE) {
        return undefined;
      }
      const next = selections.map((row) => [...row]);
      next[1] = syncCountriesAfterRegionBulkSelection(
        next[0] ?? [],
        enabledRegionNames,
        organisationCaseRegions,
        organisationCaseCountries,
        next[1] ?? [],
      );
      return next;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedLocationFilterCategory,
      organisationCaseCountries,
      organisationCaseRegions,
    ],
  );

  const handleAfterCountryBulkSelect = useCallback(
    (selections: string[][], enabledCountryNames: string[]) => {
      if (selectedLocationFilterCategory !== LocationFilterCategory.CASE) {
        return undefined;
      }
      const next = selections.map((row) => [...row]);
      next[0] = syncRegionsAfterCountryBulkSelection(
        next[1] ?? [],
        enabledCountryNames,
        organisationCaseRegions,
        organisationCaseCountries,
        next[0] ?? [],
      );
      return next;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedLocationFilterCategory,
      organisationCaseCountries,
      organisationCaseRegions,
    ],
  );

  useEffect(() => {
    const defaultTimeframeSelected = getDefaultTimeFrame();
    setSelectedTimeframe(defaultTimeframeSelected);
    setCustomSelectedTimeframe(defaultTimeframeSelected);
    if (
      timeframeTitle === undefined &&
      oldestReportSubmittedDate !== undefined
    ) {
      setTimeframeTitle(getTimeframeTitle(oldestReportSubmittedDate));
    }
    if (storedGlobalFilters.time) {
      applyTimeframeFilters(
        storedGlobalFilters.time.timeOption,
        storedGlobalFilters.time.customTimeframe,
      );
    }
    restoreLocationTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldestReportSubmittedDate]);

  useEffect(() => {
    if (
      (!storedGlobalFilters.sites || !storedGlobalFilters.countries) &&
      !locationsFirstLoad &&
      organisationSites.length
    ) {
      setSelectedSites(organisationSites);
      setSelectedCountries(organisationCountries);
      setLocationsFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationSites, organisationCountries]);

  useEffect(() => {
    setSelectedCaseCountryCodes(caseCountryCodes);
    setSelectedCaseRegionIds(caseRegionIds);
  }, [caseCountryCodes, caseRegionIds]);

  useEffect(() => {
    if (!globalFilterOpen) {
      setSelectedLocationFilterCategory(currentLocationFilterCategory);
      setLiveSelections(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterOpen]);

  const buildSectionsForCategory = (
    category: LocationFilterCategory,
  ): LocationSection[] => {
    if (category === LocationFilterCategory.CASE) {
      const liveRegionNames = liveSelections?.[0];
      const liveCountryNames = liveSelections?.[1];

      const liveCountryCodes = liveCountryNames
        ? organisationCaseCountries
            .filter((c) => liveCountryNames.includes(c.name))
            .map((c) => c.code)
        : selectedCaseCountryCodes;

      const liveRegionIds = liveRegionNames
        ? organisationCaseRegions
            .filter((r) => liveRegionNames.includes(r.name))
            .map((r) => r.id)
        : selectedCaseRegionIds;

      const {
        filteredCountryNames,
        filteredRegionNames,
        disabledCountryNames,
      } = filterCaseLocationOptions(
        organisationCaseCountries,
        organisationCaseRegions,
        liveCountryCodes,
        liveRegionIds,
      );

      const currentCountryNames =
        liveCountryNames ??
        organisationCaseCountries
          .filter((c) => selectedCaseCountryCodes.includes(c.code))
          .map((c) => c.name);

      const currentRegionNames =
        liveRegionNames ??
        organisationCaseRegions
          .filter((r) => selectedCaseRegionIds.includes(r.id))
          .map((r) => r.name);

      return [
        {
          title: 'Region',
          options: filteredRegionNames,
          selectedOptions: currentRegionNames,
          resetToState: [],
        },
        {
          title: 'Country',
          options: partitionCountryNamesEnabledFirst(
            filteredCountryNames,
            disabledCountryNames,
          ),
          selectedOptions: currentCountryNames,
          resetToState: [],
          disabledOptions: disabledCountryNames,
        },
      ];
    }
    return [
      {
        title: 'Site',
        options: organisationSites,
        selectedOptions: selectedSites,
        resetToState: organisationSites,
      },
      {
        title: 'Country',
        options: organisationCountries,
        selectedOptions: selectedCountries,
        resetToState: organisationCountries,
      },
    ];
  };

  const buildSingleSection = (): LocationSection[] => {
    const sections: LocationSection[] = [
      {
        title: 'Site',
        options: organisationSites,
        selectedOptions: selectedSites,
        resetToState: organisationSites,
      },
    ];

    if (organisationCountries.length > 0) {
      sections.push({
        title: 'Country',
        options: organisationCountries,
        selectedOptions: selectedCountries,
        resetToState: organisationCountries,
      });
    }

    return sections;
  };

  const restoreLocationTitle = () => {
    if (showTwoTabLayout) {
      const category = currentLocationFilterCategory;
      if (category === LocationFilterCategory.CASE) {
        const storedCountries = storedGlobalFilters.caseCountryCodes;
        const storedRegions = storedGlobalFilters.caseRegionIds;
        if (storedCountries || storedRegions) {
          updateLocationTitleForMultiSection(
            [
              storedCountries ?? caseCountryCodes,
              storedRegions ?? caseRegionIds,
            ],
            [caseCountryCodes, caseRegionIds],
          );
        }
      } else {
        const storedSites = storedGlobalFilters.sites;
        const storedCountries = storedGlobalFilters.countries;

        if (storedSites || storedCountries) {
          updateLocationTitleForMultiSection(
            [
              storedSites ?? organisationSites,
              storedCountries ?? organisationCountries,
            ],
            [organisationSites, organisationCountries],
          );
        }
      }
    } else {
      const storedSites = storedGlobalFilters.sites;
      const storedCountries = storedGlobalFilters.countries;
      const hasCountrySection = organisationCountries.length > 0;

      if (storedSites && storedCountries && hasCountrySection) {
        updateLocationTitleForMultiSection(
          [storedSites, storedCountries],
          [organisationSites, organisationCountries],
        );
      } else if (storedSites) {
        updateLocationTitleSingle(storedSites, organisationSites);
      } else if (storedCountries && hasCountrySection) {
        updateLocationTitleSingle(storedCountries, organisationCountries);
      }
    }
  };

  const updateLocationTitleSingle = (selected: string[], all: string[]) => {
    if (selected.length === all.length) {
      setLocationTitle(`all ${locationFilterTitle}`);
    } else if (selected.length > 3) {
      setLocationTitle(`${selected.slice(0, 3).join(', ')}...`);
    } else {
      setLocationTitle(selected.join(', '));
    }
  };

  const updateLocationTitleForMultiSection = (
    selections: string[][],
    allOptions: string[][],
  ) => {
    const allSelected = selections.every(
      (sel, i) => sel.length === allOptions[i].length,
    );
    if (allSelected) {
      setLocationTitle(`all ${locationFilterTitle}`);
    } else {
      const parts = selections
        .map((sel, i) => {
          if (sel.length === allOptions[i].length) return null;
          return `${sel.length} selected`;
        })
        .filter(Boolean);
      setLocationTitle(parts.join(', ') || `all ${locationFilterTitle}`);
    }
  };

  const applyLocationFilters = (selections: string[][]) => {
    const sections = showTwoTabLayout
      ? buildSectionsForCategory(selectedLocationFilterCategory)
      : buildSingleSection();

    const allOptions = sections.map((s) => s.options);

    if (
      showTwoTabLayout &&
      selectedLocationFilterCategory === LocationFilterCategory.CASE
    ) {
      const disabledRegionSet = new Set(sections[0]?.disabledOptions ?? []);
      const disabledCountrySet = new Set(sections[1]?.disabledOptions ?? []);
      const enabledSelections = [
        selections[0].filter((name) => !disabledRegionSet.has(name)),
        selections[1].filter((name) => !disabledCountrySet.has(name)),
      ];
      updateLocationTitleForMultiSection(enabledSelections, allOptions);
      const selectedNames = new Set(enabledSelections[1]);
      const codes = organisationCaseCountries
        .filter((c) => selectedNames.has(c.name))
        .map((c) => c.code);
      const selectedRegionNameSet = new Set(enabledSelections[0]);
      const ids = organisationCaseRegions
        .filter((r) => selectedRegionNameSet.has(r.name))
        .map((r) => r.id);
      setSelectedCaseCountryCodes(codes);
      setSelectedCaseRegionIds(ids);
      dispatchLocationUpdate(GlobalFilterLocation.CASE_COUNTRIES, codes);
      dispatchLocationUpdate(GlobalFilterLocation.CASE_REGIONS, ids);
    } else {
      updateLocationTitleForMultiSection(selections, allOptions);
      setSelectedSites(selections[0]);
      dispatchLocationUpdate(GlobalFilterLocation.SITES, selections[0]);

      if (sections.length > 1) {
        setSelectedCountries(selections[1]);
        dispatchLocationUpdate(GlobalFilterLocation.COUNTRIES, selections[1]);
      }
    }

    const categoryDispatchType =
      dashboard === InsightsDashboard.ORGANISATIONAL
        ? InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_LOCATION_FILTER_CATEGORY
        : InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_LOCATION_FILTER_CATEGORY;
    dispatch({
      type: categoryDispatchType,
      payload: selectedLocationFilterCategory,
    });

    setGlobalFilterOpen(false);
  };

  const dispatchLocationUpdate = (
    filter: GlobalFilterLocation,
    values: string[],
  ) => {
    const isOrganisational = dashboard === InsightsDashboard.ORGANISATIONAL;
    let type: InsightsGlobalFilterTypes;
    switch (filter) {
      case GlobalFilterLocation.SITES:
        type = isOrganisational
          ? InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_SITES
          : InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_SITES;
        break;
      case GlobalFilterLocation.COUNTRIES:
        type = isOrganisational
          ? InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_COUNTRIES
          : InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_COUNTRIES;
        break;
      case GlobalFilterLocation.CASE_COUNTRIES:
        type = isOrganisational
          ? InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_CASE_COUNTRY_CODES
          : InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_CASE_COUNTRY_CODES;
        break;
      case GlobalFilterLocation.CASE_REGIONS:
        type = isOrganisational
          ? InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_CASE_REGION_IDS
          : InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_CASE_REGION_IDS;
        break;
    }
    dispatch({ type, payload: values });
  };

  const applyTimeframeFilters = (
    timeOption: string,
    customTimeframe?: TimeframeInput,
  ) => {
    if (timeOption === 'custom' && customTimeframe) {
      setSelectedTimeframe(customTimeframe);
      setCustomSelectedTimeframe(customTimeframe);
      if (
        diff(customTimeframe.start, defaultTimeframeSelected.start) === 0 &&
        diff(customTimeframe.end, defaultTimeframeSelected.end) === 0
      ) {
        setTimeframeTitle(getTimeframeTitle(oldestReportSubmittedDate));
      } else {
        setTimeframeTitle(`
          ${formatDate(customTimeframe.start, DateFormats.MONTH_YEAR_LONG)} -
          ${formatDate(customTimeframe.end, DateFormats.MONTH_YEAR_LONG)}
        `);
      }
    } else {
      setTimeframeTitle(String(timeOption));
      if (getYear(timeOption) === getYear()) {
        setSelectedTimeframe({
          start: moment(timeOption, 'YYYY').startOf('year'),
          end: moment(),
        });
      } else {
        setSelectedTimeframe({
          start: moment(timeOption, 'YYYY').startOf('year'),
          end: moment(timeOption, 'YYYY').endOf('year'),
        });
      }
    }
    setGlobalFilterOpen(false);
    setSelectedTimeOption(timeOption);
    const dispatchType =
      dashboard === InsightsDashboard.ORGANISATIONAL
        ? InsightsGlobalFilterTypes.UPDATE_ORGANISATIONAL_TIME
        : InsightsGlobalFilterTypes.UPDATE_OPERATIONAL_TIME;
    dispatch({
      type: dispatchType,
      payload: { timeOption, customTimeframe },
    });
  };

  const sections = showTwoTabLayout
    ? buildSectionsForCategory(selectedLocationFilterCategory)
    : buildSingleSection();

  const locationProps = {
    type: GlobalFilterProps.LOCATION as GlobalFilterProps.LOCATION,
    sections,
    applyFilters: applyLocationFilters,
    locationFilterCategory: selectedLocationFilterCategory,
    setLocationFilterCategory: setSelectedLocationFilterCategory,
    showTwoTabLayout,
    onSelectionsChange: handleSelectionsChange,
    onCrossSelect: showTwoTabLayout ? handleCrossSelect : undefined,
    onAfterRegionBulkSelect: showTwoTabLayout
      ? handleAfterRegionBulkSelect
      : undefined,
    onAfterCountryBulkSelect: showTwoTabLayout
      ? handleAfterCountryBulkSelect
      : undefined,
  };

  const timeProps = {
    type: GlobalFilterProps.TIME as GlobalFilterProps.TIME,
    oldestSelectableYear: getYear(oldestReportSubmittedDate),
    customSelectedTimeframe,
    selectedTimeOption,
    resetToState: {
      option: 'custom',
      customTimeframe: defaultTimeframeSelected,
    },
    applyFilters: applyTimeframeFilters,
  };

  const titleHoverDisplay = sections
    .flatMap((s) => s.selectedOptions)
    .join(', ');

  return {
    globalFilterOpen,
    closeGlobalFilter: () => setGlobalFilterOpen(false),
    globalFilterProps:
      selectedGlobalFilter === GlobalFilterProps.LOCATION
        ? locationProps
        : timeProps,
    locationTitle,
    onLocationTitleClick: () => {
      setSelectedGlobalFilter(GlobalFilterProps.LOCATION);
      setGlobalFilterOpen(true);
    },
    selectedSites,
    selectedCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    locationFilterCategory: currentLocationFilterCategory,
    timeframeTitle,
    selectedTimeframe,
    onTimeTitleClick: () => {
      setSelectedGlobalFilter(GlobalFilterProps.TIME);
      setGlobalFilterOpen(true);
    },
    titleHoverDisplay,
  };
};
